import { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { shiftDate } from "@/components/cancellations/coUtils";
import { REASON_MIN } from "./ReasonDialog";
import { fmt, fmtDay } from "./scUtils";

const D = OPS.sc.reopenDialog;
const DEFAULT_DAYS = 2;

/** «إعادة فتح الجرد» (finance): a reason plus the last day the counter may
 *  still edit. The date is limited to [today, today + reopen_max_days], the
 *  server's SC_REOPEN_RANGE; it opens on today + 2 (clamped to that range).
 *  `onConfirm({reason, until})` returns a promise; the caller closes on success. */
export function ReopenDialog({ open, onOpenChange, scope, locationName, onConfirm }) {
    const { t, lang } = useI18n();
    const today = String(scope?.today || "").slice(0, 10);
    const maxDays = Number(scope?.settings?.reopen_max_days) || 14;
    const max = today ? shiftDate(today, maxDays) : "";
    const fallback = today ? shiftDate(today, Math.min(DEFAULT_DAYS, maxDays)) : "";
    const [reason, setReason] = useState("");
    const [until, setUntil] = useState(fallback);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setReason(""); setUntil(fallback); }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const dateOk = !!until && until >= today && until <= max;
    const valid = reason.trim().length >= REASON_MIN && dateOk;

    const confirm = async () => {
        setBusy(true);
        try {
            await onConfirm({ reason: reason.trim(), until });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
            <DialogContent data-testid={D.dialog} className="bg-[color:var(--bg-surface)] sm:max-w-md">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t("stk_panel_reopen_title")}{locationName ? ` · ${locationName}` : ""}</DialogTitle>
                    <DialogDescription>{t("stk_panel_reopen_body")}</DialogDescription>
                </DialogHeader>
                <label className="space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                    <span>{t("stk_admin_reason")}</span>
                    <Textarea data-testid={D.reason} value={reason} maxLength={500} rows={3}
                              onChange={(e) => setReason(e.target.value)} className="text-base" />
                    <span className="block text-[11px] font-normal text-[color:var(--ink-faint)]">{t("stk_admin_reason_min")}</span>
                </label>
                <label className="space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                    <span>{t("stk_panel_reopen_until")}</span>
                    <Input type="date" dir="ltr" lang="ar" data-testid={D.until} value={until} min={today || undefined}
                           max={max || undefined} onChange={(e) => setUntil(e.target.value)}
                           className={`h-11 text-base ${until && !dateOk ? "border-[color:var(--danger)]" : ""}`} />
                    <span className="block text-[11px] font-normal text-[color:var(--ink-faint)]">
                        {fmt(t("stk_panel_reopen_until_hint"), { max: fmtDay(max, lang) })}
                    </span>
                </label>
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={D.cancel} disabled={busy} onClick={() => onOpenChange(false)}>
                        {t("stk_admin_cancel")}
                    </Button>
                    <Button type="button" data-testid={D.confirm} disabled={!valid || busy} onClick={confirm}>
                        {busy ? t("stk_admin_saving") : t("stk_panel_reopen")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
